import moment from 'moment';
import { tryCatch } from '@bwoty-web/utils';
import { fetchJson } from '../utils/fetch';
import { getRoomDistributionString, getAgesString } from '../utils/pax/helper';
import bookingTypes from '../constants/bookingTypes';
import { getQueryKeyValue } from '../utils/url/queryHelper';
import QueryKeys from '../utils/url/queryKeys';

let hotelSearchApiUrls = null;

export const init = async (siteId, apiUrl, hotelPartId) => {
  const url = `${apiUrl}/hotelSearchControl/api/hotelSearchControlInit`;
  const hotelSearchUrls = {};

  const [error, json] = await tryCatch(async () => {
    const response = await fetch(url);
    return response.json();
  });

  if (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  const { HOTEL_SEARCH_CONTROL } = QueryKeys;

  // Prepare api urls with siteId and hotelPartId
  Object.keys(json.hotelSearchControlApiInterface).forEach((propertyName) => {
    hotelSearchUrls[propertyName] = json.hotelSearchControlApiInterface[propertyName]
      .replace('{hotelPartId}', getQueryKeyValue(HOTEL_SEARCH_CONTROL.hotelId) || hotelPartId)
      .replace('{siteId}', siteId);
  });

  hotelSearchApiUrls = hotelSearchUrls;

  return true;
};

export const getApiUrls = () => {
  return new Promise((resolve) => {
    if (hotelSearchApiUrls != null) {
      resolve(hotelSearchApiUrls);
    }
  });
};

export const getHotelPageUrls = async (apiUrl, siteId) => {
  const url = `${apiUrl}/hotelSearchControl/api/hotelPageUrls/${siteId}`;
  const [error, urls] = await tryCatch(async () => {
    const response = await fetch(url);
    return response.json();
  });

  if (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  return urls;
};

export const getHotelPageQueries = async (apiUrl, siteId) => {
  const url = `${apiUrl}/hotelSearchControl/api/hotelPageQueries/${siteId}`;
  const [error, queries] = await tryCatch(async () => {
    const response = await fetch(url);
    return response.json();
  });

  if (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  return queries;
};

export const getInitState = async (cookieId) => {
  const { HOTEL_SEARCH_CONTROL } = QueryKeys;
  const isCharter = getQueryKeyValue(HOTEL_SEARCH_CONTROL.type) !== 'IndependentPackage';

  const roomDistribution = isCharter
    ? getQueryKeyValue(HOTEL_SEARCH_CONTROL.ages)
    : getQueryKeyValue(HOTEL_SEARCH_CONTROL.roomDistribution);

  const apiUrls = await getApiUrls();

  const url = apiUrls.hotelSearchInitStateUrl
    .replace('{departureId}', getQueryKeyValue(HOTEL_SEARCH_CONTROL.departureId) || '-')
    .replace('{duration}', getQueryKeyValue(HOTEL_SEARCH_CONTROL.duration) || '-')
    .replace('{departureDate}', getQueryKeyValue(HOTEL_SEARCH_CONTROL.departureDate) || '-')
    .replace('{returnDate}', getQueryKeyValue(HOTEL_SEARCH_CONTROL.returnDate) || '-')
    .replace('{roomAges}', roomDistribution || '-')
    .replace('{sameRoom}', '-')
    .replace('{searchCharter}', isCharter)
    .replace('{cookieId}', cookieId);

  const initState = await fetchJson(url);
  return initState;
};

export const getDurations = async (departureId) => {
  const apiUrls = await getApiUrls();

  const url = apiUrls.hotelSearchDurationsUrl
    .replace('{departureId}', departureId);


  const durations = await fetchJson(url);
  return durations;
};

export const getPriceCalendar = async(departureId, hotelId, departureDate, duration, directFlight) => {
  const apiUrls = await getApiUrls();

  const url = apiUrls.packageIndependentPriceCalendarUrl
  .replace('{departureId}', departureId)
  .replace('{areaId}', '0')
  .replace('{resortId}', '0')
  .replace('{hotelPartId}', hotelId)
  .replace('{departureDate}', departureDate)
  .replace('{duration}', duration)
  .replace('{directFlight}', directFlight);
  
  const priceCalendar = fetchJson(url);

  return priceCalendar;
};

export const getDepartureDates = async (departureId, duration) => {
  const { HOTEL_SEARCH_CONTROL } = QueryKeys;
  const isCharter = getQueryKeyValue(HOTEL_SEARCH_CONTROL.type) !== 'IndependentPackage';

  const apiUrls = await getApiUrls();

  const url = apiUrls.hotelSearchDepartureDatesUrl
    .replace('{departureId}', departureId)
    .replace('{duration}', duration)
    .replace('{searchCharter}', isCharter);

  const departureDates = await fetchJson(url);
  return departureDates;
};

const getDpHotelUrl = (currentState, dpUrl) => {
  const {
    selectedDeparture,
    selectedResortId,
    selectedHotelId,
    selectedDepartureDate,
    selectedReturnDate,
    selectedDuration,
    selectedNumberOfRooms,
    rooms,
    showPriceCalendar,
    showDirectFlights,
  } = currentState;

  const departureDate = moment(selectedDepartureDate, 'YYYY-MM-DD').format('YYYYMMDD');
  const returnDate = moment(selectedReturnDate, 'YYYY-MM-DD').format('YYYYMMDD');
  const duration = selectedDuration.flexibleDuration ? -1 : selectedDuration.defaultDuration;

  const url = dpUrl
    .replace('{hotelId}', selectedHotelId)
    .replace('{itemId}', selectedHotelId)
    .replace('{resortId}', selectedResortId)
    .replace('{actionName}', '-')
    .replace('{departureId}', selectedDeparture.itemId)
    .replace('{departureDate}', departureDate)
    .replace('{returnDate}', returnDate)
    .replace('{checkInDate}', departureDate)
    .replace('{checkOutDate}', returnDate)
    .replace('{duration}', duration)
    .replace('{roomAges}', getRoomDistributionString(rooms, duration.bookingType))
    .replace('{units}', selectedNumberOfRooms)
    .replace('{ages}', getAgesString(rooms))
    .replace('{bestPrice}', showPriceCalendar ? 'true' : 'false')
    .replace('{fastestFlight}', showPriceCalendar && showDirectFlights ? 'true' : 'false');

  return url;
};

const getCharterHotelUrl = (currentState, charterUrl) => {
  const {
    selectedDeparture,
    selectedResortId,
    selectedHotelId,
    selectedDepartureDate,
    selectedDuration,
    rooms,
  } = currentState;

  const departureDate = moment(selectedDepartureDate, 'YYYY-MM-DD').format('YYYYMMDD');
  const url = charterUrl
    .replace('{hotelId}', selectedHotelId)
    .replace('{itemId}', selectedHotelId)
    .replace('{departureId}', selectedDeparture.itemId)
    .replace('{resortId}', selectedResortId)
    .replace('{departureDate}', departureDate)
    .replace('{duration}', selectedDuration.defaultDuration)
    .replace('{units}', 0)
    .replace('{ages}', getAgesString(rooms));

  return url;
};

export const getSearchUrl = async (currentState) => {
  const {
    selectedDuration,
    siteSettings,
  } = currentState;

  const { siteId, apiUrl } = siteSettings;
  const hotelPageUrls = await getHotelPageUrls(apiUrl, siteId);
  const isCharter = selectedDuration.bookingType === bookingTypes.charter;

  return isCharter
    ? getCharterHotelUrl(currentState, hotelPageUrls.charterSearch)
    : getDpHotelUrl(currentState, hotelPageUrls.independentSearch);
};

export const getSearchUrlIdun = async (currentState) => {
  const {
    selectedDuration,
    siteSettings,
  } = currentState;
  
  const { siteId, apiUrl } = siteSettings;
  const hotelPageQueries = await getHotelPageQueries(apiUrl, siteId);
  const isCharter = selectedDuration.bookingType === bookingTypes.charter;

  const searchQuery = isCharter
    ? getCharterHotelUrl(currentState, hotelPageQueries.charterSearch)
    : getDpHotelUrl(currentState, hotelPageQueries.independentSearch);

  const searchUrl = window.location.pathname + searchQuery;
  return searchUrl;
};
